import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import styles from "./Dashboard.module.css";
import cn from 'classnames';
import SidebarLayout from '../../../component/common/Layout/SidebarLayout/SidebarLayout';
import DownloadsOverChart from '../../../component/UI/DownloadsOverChart/DownloadsOverChart';
import RevenueChart from '../../../component/UI/RevenueChart/RevenueChart';
import UserTable from './component/UserTable/UserTable';
import SearchInput from '../../../component/common/SearchInput/SearchInput';
import TextHeader from '../../../component/common/TextHeader/TextHeader';
import Tag, { ITagSize, ITagTheme } from '../../../component/common/Tag/Tag';
import OverviewCard, { IDifferenceType } from '../../../component/common/OverviewCard/OverviewCard';
import { ASSESTS } from '../../../constant/assest';
import CustomButton from '../../../component/common/CustomButton/CustomButton';
import {Image} from 'react-bootstrap';
import FilterSelect from '../../../component/common/FilterSelect/FilterSelect';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchDashboardAnalytics } from '../../../redux/slices/dashboard/actions';
import { SubscriptionStatus } from '../../../constant/enums';
import OverviewCardSkeleton from '../../../component/common/OverviewCard/OverviewCardSkeleton';
import { capitalizedUserName } from '../../../utils';

const Dashboard: FunctionComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { perFormanceData, loading } = useSelector((state: RootState) => state.dashboard);
    const { user } = useSelector((state: RootState) => state.auth);
    const [inputValue, setInputValue] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [status, setStatus] = useState<SubscriptionStatus | 'all'>('all');
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const [timeAgo, setTimeAgo] = useState('');
    const [refreshCount, setRefreshCount] = useState(0);
    
    useEffect(() => {
        dispatch(fetchDashboardAnalytics());
        setLastUpdated(new Date());
    }, [dispatch]);

    useEffect(() => {
        if (!lastUpdated) return;

        const calculateTimeAgo = () => {
            const minutes = Math.floor((new Date().getTime() - lastUpdated.getTime()) / 60000);
            if (minutes < 1) {
                setTimeAgo('just now');
            } else if (minutes === 1) {
                setTimeAgo('1 minute ago');
            } else {
                setTimeAgo(`${minutes} minutes ago`);
            }
        };

        calculateTimeAgo();
        const intervalId = setInterval(calculateTimeAgo, 30000); // Update every 30 seconds

        return () => clearInterval(intervalId);
    }, [lastUpdated]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(inputValue.trim());
        }, 500);
    
        return () => {
            clearTimeout(handler);
        };
    }, [inputValue]);

    const handleRefresh = useCallback(() => {
        dispatch(fetchDashboardAnalytics());
        setLastUpdated(new Date());
        setRefreshCount(prev => prev + 1);
    }, [dispatch]);

    const handleReset = useCallback(() => {
        setInputValue("");
        setStatus('all');
    }, []);

    const overView = useMemo(() => perFormanceData ? [
        {title: 'Total Users', value: perFormanceData.total_users_count?.toLocaleString(), icon: ASSESTS.USERS, differenceType: perFormanceData.total_users_growth >= 0 ? IDifferenceType.INCREASE : IDifferenceType.DECREASE, differenceText: `${perFormanceData.total_users_growth}% from last month`},
        {title: 'Android App Downloads (Approx.)', value: perFormanceData.app_downloads.toLocaleString(), icon: ASSESTS.DOWNLOAD },
        {title: 'Free users', value: perFormanceData.free_users_count?.toLocaleString(), icon: ASSESTS.USERONLINE, differenceType: IDifferenceType.INCREASE, differenceText: `${perFormanceData.free_users_share}% of last month`},
        {title: 'Pro Users', value: perFormanceData.pro_users.toLocaleString(), icon: ASSESTS.CROWN, differenceType: IDifferenceType.INCREASE, differenceText: `${perFormanceData.pro_users_share}% of last month`},
        {title: 'Monthly Website Subscriptions', value: perFormanceData.web_monthly_subscriptions?.toLocaleString(), icon: ASSESTS.SUBSCRIPTION, differenceType: perFormanceData.monthly_subscriptions_growth >= 0 ? IDifferenceType.INCREASE : IDifferenceType.DECREASE, differenceText: `${perFormanceData.monthly_subscriptions_growth}% from last month`},
        {title: 'Yearly Website Subscriptions', value: perFormanceData.web_yearly_subscriptions?.toLocaleString(), icon: ASSESTS.DOWNLOAD, differenceType: IDifferenceType.INCREASE, differenceText: `${perFormanceData.yearly_subscriptions_share}% of last month`},
        {title: 'Monthly App Subscriptions', value: perFormanceData.monthly_subscriptions.toLocaleString(), icon: ASSESTS.SUBSCRIPTION, differenceType: perFormanceData.monthly_subscriptions_growth >= 0 ? IDifferenceType.INCREASE : IDifferenceType.DECREASE, differenceText: `${perFormanceData.monthly_subscriptions_growth}% from last month`},
        {title: 'Yearly App Subscriptions', value: perFormanceData.yearly_subscriptions.toLocaleString(), icon: ASSESTS.DOWNLOAD, differenceType: IDifferenceType.INCREASE, differenceText: `${perFormanceData.yearly_subscriptions_share}% of last month`},
    ] : [], [perFormanceData]);
    return (
        <SidebarLayout>
            <div className={cn(styles.dasboardTopRow)}>
                <div className={cn(styles.dasboardTopRowHeader)}>
                    <TextHeader 
                        title={'Dashboard Overview'} 
                        summary={`Welcome back, ${capitalizedUserName(user?.full_name).split(' ')[0]}! Here's your Fishinity app overview.`}
                        className='mb-4'
                    />
                    <div className={styles.updateLabelWrap}>
                        <button className={styles.refreshBtn} onClick={handleRefresh}>
                            <Image src={ASSESTS.REFRESH} alt="Refresh" />
                        </button>
                        <Tag 
                            label={loading === 'pending' ? 'Updating...' : `Last updated: ${timeAgo}`}
                            theme={ITagTheme.SUCCESS}
                            size={ITagSize.SMALL}                         
                        />
                    </div>
                      
                </div>  
                <div className={styles.dashBoardCardsRow}>
                    {loading === 'pending' ? (
                        Array.from({ length: 6 }).map((_, index) => (
                            <OverviewCardSkeleton 
                                key={index}
                                className={styles.dashboardOverViewCard} 
                            />
                        ))
                    ) : (
                        loading === 'succeeded' && overView.map((item, index) => (
                            <OverviewCard 
                                key={`${item.title}-${index}`}
                                className={styles.dashboardOverViewCard} 
                                title={item.title} 
                                value={item.value} 
                                icon={item.icon}
                                differenceType={item.differenceType} 
                                differenceText={item.differenceText} 
                            />
                        ))
                    )}
                </div> 
                <div className={styles.dashboardChartsNew}>
                    <div className={styles.lineChartNew}>
                        <DownloadsOverChart refresh={refreshCount} />
                    </div>
                    <div className={styles.barChartNew}>
                        <RevenueChart refresh={refreshCount} />
                    </div>
                </div>
            </div>
            <div className={cn(styles.dasboardBottomRow)}>
                <TextHeader 
                    title={'Fishinity Accounts'} 
                    className='mb-2'
                />
                <div className={styles.tableFilter}>
                    <SearchInput placeholder='Search by Name or Email...' className={styles.dashboardSearch} value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
                    <div className={styles.selectFilter}>
                        <FilterSelect 
                            options={[
                                { value: "all", label: "All Status" },
                                ...Object.values(SubscriptionStatus).map(status => ({ value: status, label: status.charAt(0).toUpperCase() + status.slice(1) }))
                            ]} 
                            onSelect={(value: string) => setStatus(value as SubscriptionStatus | 'all')} 
                            placeholder={'Status'} 
                            value={status}
                        />
                    </div>
                    <div className={styles.deselectFilter}>
                        <CustomButton className={styles.deselectBtn} onClick={handleReset}>
                            Reset All
                        </CustomButton>
                    </div>
                </div>
                <UserTable searchTerm={debouncedSearchTerm} status={status} refresh={refreshCount} />
            </div>
        </SidebarLayout>
    );
};

export default Dashboard;